import { useMutation } from '@apollo/client';
import { UpdateCommentDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { UpdateCommentTRPCInput } from './useUpdateCommentTRPC';
import { useUpdateCommentTRPC } from './useUpdateCommentTRPC';

/**
 * Flag-gated wrapper (RSP-6496). Both paths return `{ Id }`.
 *
 * A non-author edit is denied server-side: the Apollo path rejects with a
 * `permission-error` GraphQL extension, the tRPC path with a `FORBIDDEN`
 * `TRPCClientError`. The wrapper surfaces the raw rejection unchanged — the
 * consumer (`useComments.tinycomments_edit_comment`) classifies EITHER shape
 * as `canEdit: false` via `isPermissionError` / `isTrpcPermissionError`.
 */
export const useUpdateComment = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateCommentGraphQL, graphqlState] = useMutation(
    UpdateCommentDocument
  );

  const trpcMutation = useUpdateCommentTRPC();

  const updateComment = async (
    input: UpdateCommentTRPCInput
  ): Promise<{ Id: string }> => {
    if (trpcEnabled) {
      return trpcMutation.updateComment(input);
    }

    const result = await updateCommentGraphQL({
      variables: { Id: input.Id, Content: input.Content },
    });
    if (!result.data?.update_comment_by_pk) {
      throw new Error('Failed to update comment');
    }

    return { Id: result.data.update_comment_by_pk.Id };
  };

  if (trpcEnabled) {
    return {
      updateComment,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateComment,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
